import {
  Home,
  Banana,
  Scissors,
  ShoppingBag,
  BarChart3,
  TrendingUp,
  LayoutDashboard,
  Sun,
  CalendarRange,
  DollarSign,
  Monitor,
  Users,
  Truck,
  Sprout,
  Kanban,
  ClipboardCheck,
  Droplets,
  Handshake,
  Wallet,
  Users2,
  Megaphone,
  Store,
  FileText,
  Shield,
  Building2,
  MapPin,
  MessageSquare,
  Grid3x3,
  type LucideIcon,
} from "lucide-react";

import { resolverModuloId, modulosVisiveis, modulosDoUsuario, HOME_POR_MODULO, type ModuleId } from "@shared/modulos";
import { podeFazer, type Capacidade } from "@shared/permissoes";
import { OFERTAS_ATIVAS } from "@shared/mercado-ofertas";

export type { ModuleId };
export {
  ehRotaAdministracao, rotaDefineModulo, ROTAS_ADMINISTRACAO, MODULO_PADRAO,
  moduloExigidoPelaRota, rotaInicialDoUsuario, HOME_POR_MODULO,
} from "@shared/modulos";

export interface ModuleNavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  /**
   * A capacidade que a tela exige (shared/permissoes.ts). Item sem `cap` é de
   * todo mundo. É o que o sidebar e a tabbar filtram com `podeFazer` — o único
   * eixo de papel do menu (`adminOnly`/`permissaoId` saíram no PR 4).
   */
  cap?: Capacidade;
  /**
   * Item que só existe para um lado do Mercado. NÃO é o filtro de VISIBILIDADE do
   * módulo — esse continua saindo inteiro de `modules_enabled`, e as duas orgs veem
   * o mesmo "Mercado" no seletor. Aqui é só o menu de dentro: "Corretores" não faz
   * sentido para um corretor, e "Romaneios recebidos" não faz sentido para o
   * produtor (os dele estão no Cacho, e duplicá-los aqui seria a segunda lista de
   * vendas que este ciclo existe para não criar).
   */
  orgTipo?: "PRODUTOR" | "COMPRADOR";
  /**
   * Item que só existe para um PERFIL de org compradora (C8b). Um eixo a mais
   * que `orgTipo`, e pelo mesmo motivo: "A receber" não faz sentido para o
   * DISTRIBUIDOR — ele não revende a carga, ele a recebe, e o que ele deve já
   * aparece em "A pagar".
   *
   * ⚠️ É MENU, não acesso. O gate de verdade é `requireCorretor` no servidor,
   * que responde 403 `PERFIL_INVALIDO`; esconder o item só evita oferecer uma
   * tela que responderia erro.
   */
  perfilComprador?: "CORRETOR" | "DISTRIBUIDOR";
  /**
   * Badge de contagem no submenu e na tabbar (só o Mercado do produtor):
   * `receber` = romaneios vencendo em 7 dias (até o C4, quando passa a ser
   * "pagamentos informados a confirmar"); `ofertas` = respostas novas (PR 3).
   */
  badge?: "receber" | "ofertas" | "romaneios";
  /**
   * Item de uma feature DESLIGADA — some do menu lateral e da tabbar.
   *
   * ⚠️ Esconder o item não desliga nada sozinho: a rota e a API têm de recusar
   * junto (é a mesma regra dos módulos no CLAUDE.md). Isto aqui é só a metade
   * visível — a outra está em `App.tsx` e em `server/routes/mercado/index.ts`.
   */
  desativado?: boolean;
}

export interface RizomaModule {
  id: ModuleId;
  label: string;
  tagline: string;
  /** Só o Mercado: os dois lados veem o mesmo módulo, e não a mesma coisa. */
  taglineCorretor?: string;
  icon: LucideIcon;
  homePath: string;
  matchPrefixes: string[];
  nav: ModuleNavItem[];
}

export const MODULES: RizomaModule[] = [
  {
    id: "cacho",
    label: "Cacho",
    tagline: "colheitas e fitas",
    icon: Banana,
    homePath: HOME_POR_MODULO.cacho,
    matchPrefixes: ["/cacho"],
    nav: [
      { path: "/cacho/inicio",     label: "Início",             icon: Home },
      { path: "/cacho/mangaras",   label: "Mangarás",           icon: Scissors },
      { path: "/cacho/colheitas",  label: "Colheitas",          icon: ShoppingBag },
      { path: "/cacho/vendas",     label: "Vendas",             icon: Truck,           cap: "cacho.vendas.ver" },
      { path: "/cacho/ciclos",     label: "Controle de Fitas",  icon: BarChart3 },
      { path: "/cacho/relatorios", label: "Relatórios",         icon: TrendingUp },
      { path: "/cacho/dashboard",  label: "Dashboard",          icon: LayoutDashboard },
    ],
  },
  {
    id: "equipe",
    label: "Equipe",
    tagline: "mão de obra e diárias",
    icon: Users,
    homePath: HOME_POR_MODULO.equipe,
    matchPrefixes: ["/equipe"],
    nav: [
      { path: "/equipe",            label: "Dashboard",       icon: LayoutDashboard },
      { path: "/equipe/hoje",       label: "Hoje no Campo",   icon: Sun },
      { path: "/equipe/plano",      label: "Plano da Semana", icon: CalendarRange },
      { path: "/equipe/tv",         label: "Modo TV",         icon: Monitor },
      { path: "/equipe/financeiro", label: "Financeiro",      icon: DollarSign, cap: "equipe.folha.ver" },
      { path: "/equipe/relatorios", label: "Relatórios",      icon: BarChart3,  cap: "equipe.folha.ver" },
      // Esforço (diárias), não custo — mesmo gate de Relatórios.
      { path: "/equipe/alocacao",   label: "Alocação",        icon: Grid3x3,    cap: "equipe.folha.ver" },
    ],
  },
  {
    id: "manejo",
    label: "Manejo",
    tagline: "operações",
    icon: Sprout,
    // O Comando (chat) saiu daqui pra /agente na Etapa 1 do agente — Operações
    // passa a ser a porta de entrada do módulo.
    homePath: HOME_POR_MODULO.manejo,
    matchPrefixes: ["/manejo"],
    nav: [
      { path: "/manejo/operacoes", label: "Operações",  icon: Kanban },
      // Logo abaixo de Operações (spec §4). A Irrigação HERDA o gating do Manejo —
      // sem flag nova e sem entrada em plans.ts: mora em /manejo e em /api/manejo.
      { path: "/manejo/irrigacao", label: "Irrigação",  icon: Droplets },
      // Programa continua até o ciclo 4, quando vira Receitas.
      { path: "/manejo/programa",  label: "Programa",   icon: CalendarRange, cap: "manejo.planejar" },
      // Estoque SAIU (Operações v2): o saldo mora no ERP, por fora. Conciliação
      // é de lá — o Rizoma nunca teve o saldo como fonte da verdade, só um cache.
      // Auditoria mora dentro do Manejo — não é módulo próprio, usa o mesmo
      // gate "manejo". Auditorias e o Dashboard (consulta frequente) ficam
      // aqui; Critérios auditoria e Faixas de nota são cadastro — foram pra
      // Administração (client/src/components/app-sidebar.tsx), junto de
      // Funcionários e Atividades.
      { path: "/manejo/auditoria",           label: "Auditorias",           icon: ClipboardCheck, cap: "auditoria.ver" },
      { path: "/manejo/auditoria/dashboard", label: "Dashboard de auditoria", icon: BarChart3, cap: "auditoria.ver" },
    ],
  },
  {
    id: "mercado",
    label: "Mercado",
    // O que o módulo é para o PRODUTOR. O corretor vê outra coisa no mesmo
    // módulo — ver `taglineDoModulo`.
    // Curto de propósito: o seletor da sidebar tem ~150px e truncava
    // "cotações, ofertas e corr…". Ofertas entra na lista quando existir de fato.
    tagline: "cotações e compradores",
    taglineCorretor: "cargas e romaneios",
    icon: Handshake,
    // A HOME é a mesma rota para os dois lados; quem decide o conteúdo é a página,
    // conforme `organizations.tipo`. O registry NÃO ganha filtro por tipo: a
    // visibilidade continua saindo de `modules_enabled`, e um segundo eixo aqui
    // dobraria as combinações a testar sem responder nada que a página não responda.
    homePath: HOME_POR_MODULO.mercado,
    matchPrefixes: ["/mercado"],
    // PRODUTOR: cinco itens, cada um respondendo UMA pergunta — Início ("o que
    // preciso fazer?"), Cotações ("quanto a praça paga?"), Ofertas ("quem quer
    // minha carga?"), Corretores ("quem é?") e A receber ("quanto me devem?").
    // Nada duplica o Cacho: formalizar continua sendo o card na venda.
    nav: [
      { path: "/mercado",            label: "Início",     icon: Home },
      { path: "/mercado/cotacoes",   label: "Cotações",   icon: TrendingUp, orgTipo: "PRODUTOR", cap: "mercado.ver" },
      { path: "/mercado/ofertas",    label: "Ofertas",    icon: Megaphone,  orgTipo: "PRODUTOR", badge: "ofertas", desativado: !OFERTAS_ATIVAS, cap: "mercado.ver" },
      { path: "/mercado/compradores", label: "Compradores", icon: Users2,    orgTipo: "PRODUTOR", cap: "mercado.ver" },
      { path: "/mercado/receber",    label: "A receber",  icon: Wallet,     orgTipo: "PRODUTOR", badge: "receber", cap: "mercado.ver" },
      // O COMPRADOR: Cargas primeiro, porque é o que ele FAZ — montar caminhão
      // é o trabalho do dia, e Praça/Romaneios são consulta. `Cargas` vem antes
      // de `Praça` desde o C5a por isso; até ali a caderneta era só leitura e a
      // Praça, a tela que mudava todo dia.
      //
      // "Romaneios" continua existindo e não virou aba de Cargas: é a lista
      // COMPLETA do que foi formalizado para ele, carga ou não — e romaneio
      // fora de carga não apareceria em lugar nenhum se ela saísse.
      //
      // `A receber` (C8b) é só do CORRETOR — distribuidor não revende a carga, e
      // uma tela do que ele tem a receber mostraria um número que não existe na
      // operação dele. É a MESMA rota do "A receber" do produtor: os dois lados
      // do balcão, uma URL só.
      // "Romaneios" vem PRIMEIRO no comprador, logo depois do Início: é a lista
      // completa do que foi formalizado para ele, e é por onde ele confere o que
      // chegou antes de montar qualquer carga.
      //
      // O rótulo perdeu o "Notas /" no Início v2: com o badge de "a conferir" ao
      // lado, duas palavras e um número não cabem na tabbar de 390px — e
      // "romaneio" é como o documento sai impresso, que é o que ele procura.
      { path: "/mercado/romaneios",  label: "Romaneios",  icon: FileText,   orgTipo: "COMPRADOR", badge: "romaneios" },
      { path: "/mercado/cargas",     label: "Cargas",     icon: Truck,      orgTipo: "COMPRADOR" },
      { path: "/mercado/pagar",      label: "A pagar",    icon: Wallet,     orgTipo: "COMPRADOR" },
      // ⚠️ ESCONDIDO, não desligado (13/09/2026). O corretor ainda não tem o que
      // confirmar aqui — nenhuma carga foi entregue —, e um item que abre em zero
      // toda vez ensina a ignorá-lo. A rota e a API continuam DE PÉ de propósito:
      // é só o menu, então quem tiver o link ainda entra e nada precisa ser
      // religado quando a primeira carga for entregue. Devolver é apagar esta
      // linha e o comentário.
      // { path: "/mercado/receber",    label: "A receber",  icon: Banknote,   orgTipo: "COMPRADOR", perfilComprador: "CORRETOR" },
      // (ao devolver, reponha `Banknote` no import do lucide-react acima.)
      // Cotações aparece DUAS vezes no registry, uma por lado — mesmo caminho,
      // posições diferentes. Não é duplicação por descuido: a ORDEM é a
      // prioridade (é ela que decide o corte da tabbar no celular), e as duas
      // prioridades são honestamente diferentes. Para o produtor, o preço da
      // praça é a primeira pergunta do dia, logo depois do Início. Para o
      // comprador, o dia é montar carga e pagar; a placa é consulta, e fica
      // depois — no lugar que a Praça deixou, que é onde ele já olhava.
      { path: "/mercado/cotacoes",   label: "Cotações",   icon: TrendingUp, orgTipo: "COMPRADOR" },
      // A Praça é o outro lado das Ofertas: sem elas, é uma tela vazia com filtro.
      { path: "/mercado/praca",      label: "Praça",      icon: Store,      orgTipo: "COMPRADOR", desativado: !OFERTAS_ATIVAS },
      { path: "/mercado/produtores", label: "Produtores", icon: Users2,     orgTipo: "COMPRADOR" },
    ],
  },
  {
    // O módulo de quem administra o RIZOMA (`users.plataforma_admin`). Não é
    // módulo de org: `visibleModules` só o acrescenta pela flag do usuário, e
    // `modules_enabled` nunca o carrega. As telas de Cotações/Praças/Organizações
    // moravam soltas num grupo "Plataforma" do sidebar sob /admin — agora são o
    // nav deste módulo, com redirect dos caminhos antigos no App.tsx.
    id: "plataforma",
    label: "Plataforma",
    tagline: "fazendas e praça",
    icon: Shield,
    homePath: HOME_POR_MODULO.plataforma,
    matchPrefixes: ["/plataforma"],
    nav: [
      { path: "/plataforma",              label: "Visão geral",       icon: LayoutDashboard },
      { path: "/plataforma/fazendas",     label: "Fazendas",          icon: Building2 },
      { path: "/plataforma/cotacoes",     label: "Cotações da praça", icon: TrendingUp },
      { path: "/plataforma/pracas",       label: "Praças",            icon: MapPin },
      // O pré-cadastro de comprador sem usuário (§B) — outra pergunta que a
      // lista de Fazendas: "quem da praça ainda não tem conta?".
      { path: "/plataforma/organizacoes", label: "Pré-cadastro",      icon: Users2 },
      // Os avisos automáticos do número do Rizoma (GZappy): modelos, envios, regras.
      { path: "/plataforma/mensagens",    label: "Mensagens WhatsApp", icon: MessageSquare },
    ],
  },
];

/**
 * Detecta o módulo atual a partir do location do Wouter.
 *
 * `moduloAtual` é o que está selecionado agora: em rota de ADMINISTRAÇÃO ele é
 * mantido, em vez de a rota inferir um módulo. Sem isso, abrir uma tela de
 * Administração estando no Manejo jogava o usuário no Cacho — a rota não batia
 * com nenhum prefixo de módulo e caía no fallback. A regra pura mora em
 * shared/modulos.ts.
 */
/**
 * Os itens de nav que ESTE usuário deve ver — o filtro de público.
 *
 * ⚠️ Existe porque a gaveta e a tabbar já faziam isto separados, com o comentário
 * de uma dizendo que era "o MESMO filtro" da outra. Duas cópias de um filtro de
 * visibilidade é como uma feature desligada continua aparecendo num dos dois —
 * e no celular, que é onde o app é usado de verdade, a tabbar é o menu.
 *
 * Isto é o eixo de público (tipo de org, perfil), o desligamento e — desde o
 * PR 2 — o eixo de PAPEL (`cap`). Não existe mais filtro só da gaveta.
 */
export function itensDoPublico(
  nav: ModuleNavItem[],
  quem: { orgTipo?: "PRODUTOR" | "COMPRADOR" | null; perfilComprador?: string | null; role?: string | null },
): ModuleNavItem[] {
  return nav.filter(i => {
    if (i.desativado) return false;
    // O eixo de PAPEL (PR 2): item com `cap` só para quem a tem. Sem `role`
    // (chamada antiga) o filtro não corta — quem esconde é quem sabe o papel.
    if (i.cap && quem.role !== undefined && !podeFazer(quem.role, i.cap)) return false;
    // Item sem `orgTipo` (a Início) serve aos dois lados; o resto é de um só.
    if (i.orgTipo && i.orgTipo !== (quem.orgTipo ?? "PRODUTOR")) return false;
    if (i.perfilComprador && i.perfilComprador !== quem.perfilComprador) return false;
    return true;
  });
}

export function getCurrentModule(
  location: string,
  moduloAtual?: ModuleId | null,
  visiveis?: ModuleId[],
): RizomaModule {
  const id = resolverModuloId(location, moduloAtual, visiveis);
  return MODULES.find((m) => m.id === id)!;
}

/**
 * Módulos visíveis para a org — 100% a partir de `modules_enabled`.
 *
 * ⚠️ "Cacho é sempre base" SAIU daqui (Mercado C2). Enquanto toda org era produtora,
 * cravar o Cacho era inofensivo; com a org de COMPRADOR, que só tem 'mercado', aquilo
 * passaria a mostrar o menu inteiro do Cacho para quem não tem nem os dados nem a
 * permissão. A migração 055 garante 'cacho' em toda org PRODUTOR justamente para que
 * esta função possa ser uma leitura pura da coluna — a premissa é garantida no banco,
 * não presumida aqui.
 */
export function visibleModules(modulesEnabled: string[], plataformaAdmin = false): RizomaModule[] {
  // A Plataforma entra por OUTRO eixo (a flag do usuário), sempre por último.
  const ids = modulosDoUsuario(modulosVisiveis(modulesEnabled), plataformaAdmin);
  return ids
    .map(id => MODULES.find(m => m.id === id))
    .filter((m): m is RizomaModule => !!m);
}

/**
 * O subtítulo do módulo no seletor da sidebar.
 *
 * Só o Mercado varia: para o produtor ele é "cotações, ofertas e corretores"
 * (as três perguntas que o módulo passou a responder no PR 1 da UI); para a org
 * de COMPRADOR continua sendo a caderneta de romaneios do C2. Os demais módulos
 * têm um subtítulo só e ignoram o tipo.
 */
export function taglineDoModulo(mod: RizomaModule, orgTipo?: "PRODUTOR" | "COMPRADOR"): string {
  return orgTipo === "COMPRADOR" && mod.taglineCorretor ? mod.taglineCorretor : mod.tagline;
}

/** Chave usada para persistir o último módulo visitado. */
export const LAST_MODULE_KEY = "rizoma:last-module";
